import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./VaultWrappedPaywall.module.scss"

/**
 * VaultWrappedPaywall — an original, self-contained NON-ONBOARDING paywall.
 *
 * Concept ("Vault Wrapped — Your Year, Sealed"): every other paywall in this
 * app is a single static scene. This one is a moonshot on FORMAT — a
 * Spotify-Wrapped / Instagram-Stories style, multi-chapter, auto-advancing
 * STORY that recaps the user's year inside their private vault. Tap-through
 * (or auto-advance) progress bars at the top, full-bleed kinetic chapters that
 * crossfade, and a crescendo: by the time the offer lands, the user has spent
 * ~12s admiring everything THEY built and protected this year, so the ask is
 * framed as "keep it going," not a cold pitch. That earned investment
 * (endowment effect) is the conversion engine.
 *
 * The final chapter funnels to the YEARLY plan: the annual price is the single
 * most prominent price for the annual product (App Store-compliant), a free
 * trial removes friction, savings vs monthly is shouted, and monthly is demoted
 * to one quiet line of text. It is a pure aspirational/celebratory upsell — it
 * never implies the user loses access to anything they've saved.
 *
 * Fully self-contained: no shared paywall components are reused. It reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data. Reuses the
 * brand fonts (RedHatDisplay + KronaOne for the wordmark/stat numbers), the
 * brand primary blue and secondary gold, existing repo feature icons, and
 * Unsplash "memory" photos bundled under /img/vaultWrapped (with a gradient
 * fallback so every tile still reads if a photo fails to load).
 */

const MEMORY_COUNT = 10
const CHAPTER_COUNT = 5

const VaultWrappedPaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData
  const reduceMotion = !!useReducedMotion()

  const [chapter, setChapter] = useState(0)
  const [paused, setPaused] = useState(false)
  const [dir, setDir] = useState(1)
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")

  const isOffer = chapter === CHAPTER_COUNT - 1

  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setHeaderInsetColor("#0a0b14")
    actions.setFooterInsetColor("#0a0b14")
  }, [actions])

  /* ---- pricing (read from real debug product data) ---- */
  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const perMonthEquivalent = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.round((1 - ratio) * 100)
  }, [yearlyProduct, monthlyProduct])

  /* ---- "your year" stats (real data when present, else evocative debug) ---- */
  const memories =
    productData?.numPhotos && productData.numPhotos > 0
      ? productData.numPhotos
      : 1247
  const albums =
    productData?.numAlbums && productData.numAlbums > 0
      ? productData.numAlbums
      : 38

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  /* ---- chapter durations (ms); offer chapter never auto-advances ---- */
  const durations = useMemo(() => [2800, 3600, 3600, 3200, 0], [])

  const go = useCallback((next: number) => {
    if (next < 0 || next > CHAPTER_COUNT - 1) return
    setDir(next >= 0 ? 1 : -1)
    setChapter((c) => {
      setDir(next > c ? 1 : -1)
      return next
    })
  }, [])

  /* auto-advance */
  useEffect(() => {
    if (isOffer || paused) return
    const t = setTimeout(() => go(chapter + 1), durations[chapter])
    return () => clearTimeout(t)
  }, [chapter, paused, isOffer, durations, go])

  /* ---- tap / press-and-hold navigation (chapters 0..3) ---- */
  const downRef = useRef<{ t: number; x: number } | null>(null)

  const onPointerDown = (e: React.PointerEvent) => {
    downRef.current = { t: e.timeStamp, x: e.clientX }
    setPaused(true)
  }
  const onPointerUp = (e: React.PointerEvent) => {
    setPaused(false)
    const d = downRef.current
    downRef.current = null
    if (!d) return
    const held = e.timeStamp - d.t
    if (held > 260) return // it was a hold-to-pause, not a tap
    const w = (e.currentTarget as HTMLElement).clientWidth
    if (e.clientX - d.x < -40 || e.clientX < w * 0.28) {
      go(chapter - 1)
    } else {
      go(chapter + 1)
    }
  }
  const onPointerCancel = () => {
    downRef.current = null
    setPaused(false)
  }

  const handleClose = () => native.postNativeEvent("close")
  const handlePurchase = () => {
    const product: Product | undefined =
      selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={`${styles.screen} ${styles[`bg${chapter}`]}`}>
      <div className={styles.grain} aria-hidden />
      <div className={styles.vignette} aria-hidden />

      <div className={styles.topBar} style={{ paddingTop: topInset + 12 }}>
        <ProgressBar
          chapter={chapter}
          durations={durations}
          paused={paused}
          reduceMotion={reduceMotion}
        />
        <div className={styles.topRow}>
          <span
            className={styles.wrappedMark}
            style={{ opacity: isOffer ? 0 : 1 }}
          >
            <span className={styles.markDot} />
            VAULT&nbsp;WRAPPED
          </span>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      </div>

      <div className={styles.stage}>
        <AnimatePresence custom={dir}>
          <motion.div
            key={chapter}
            className={styles.chapter}
            custom={dir}
            initial={{ opacity: 0, scale: reduceMotion ? 1 : 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: reduceMotion ? 1 : 0.98 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            {chapter === 0 && <ChapterIntro reduceMotion={reduceMotion} />}
            {chapter === 1 && (
              <ChapterVolume
                memories={memories}
                albums={albums}
                reduceMotion={reduceMotion}
              />
            )}
            {chapter === 2 && <ChapterProtection reduceMotion={reduceMotion} />}
            {chapter === 3 && <ChapterStatus reduceMotion={reduceMotion} />}
            {chapter === 4 && (
              <ChapterOffer
                hasTrial={hasTrial}
                yearlyPrice={yearlyPrice}
                perMonthEquivalent={perMonthEquivalent}
                monthlyPrice={monthlyPrice}
                savings={savings}
                selected={selected}
                setSelected={setSelected}
                onPurchase={handlePurchase}
                topInset={topInset}
                bottomInset={bottomInset}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {!isOffer && (
        <div
          className={styles.touchLayer}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onPointerLeave={onPointerCancel}
        >
          <span className={styles.tapHint}>Tap to continue</span>
        </div>
      )}
    </div>
  )
}

/* ================================================================== */
/* Progress bars — IG-stories style segments that fill over each       */
/* chapter's duration and pause on press-hold.                          */
/* ================================================================== */
const ProgressBar = ({
  chapter,
  durations,
  paused,
  reduceMotion,
}: {
  chapter: number
  durations: number[]
  paused: boolean
  reduceMotion: boolean
}) => (
  <div className={styles.progress}>
    {Array.from({ length: CHAPTER_COUNT }, (_, i) => {
      const done = i < chapter || chapter === CHAPTER_COUNT - 1
      const active = i === chapter && chapter !== CHAPTER_COUNT - 1
      return (
        <span key={i} className={styles.segment}>
          <span
            key={active ? `a${chapter}` : `s${i}-${done}`}
            className={`${styles.fill} ${done ? styles.fillDone : ""} ${
              active ? styles.fillActive : ""
            }`}
            style={
              active && !reduceMotion
                ? {
                    animationDuration: `${durations[i]}ms`,
                    animationPlayState: paused ? "paused" : "running",
                  }
                : undefined
            }
          />
        </span>
      )
    })}
  </div>
)

/* ================================================================== */
/* Chapter 0 — cold open                                                */
/* ================================================================== */
const ChapterIntro = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <div className={styles.center}>
    <motion.div
      className={styles.vaultGlyph}
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className={styles.glyphRing} aria-hidden />
      <div className={styles.glyphRing2} aria-hidden />
      <ShieldGlyph className={styles.glyphIcon} />
    </motion.div>

    <Reveal delay={0.35}>
      <span className={styles.eyebrow}>2025 · A YEAR IN YOUR VAULT</span>
    </Reveal>
    <Reveal delay={0.5}>
      <h1 className={styles.huge}>
        Your year,
        <br />
        <span className={styles.accentText}>sealed.</span>
      </h1>
    </Reveal>
    <Reveal delay={0.7}>
      <p className={styles.lede}>
        A quick look back at everything you kept private this year — and just
        for you.
      </p>
    </Reveal>
    {!reduceMotion && (
      <motion.span
        className={styles.swipeUp}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0.4] }}
        transition={{ delay: 1.1, duration: 1.4, repeat: Infinity }}
      >
        ›
      </motion.span>
    )}
  </div>
)

/* ================================================================== */
/* Chapter 1 — volume of memories (count-up + floating montage)         */
/* ================================================================== */
const ChapterVolume = ({
  memories,
  albums,
  reduceMotion,
}: {
  memories: number
  albums: number
  reduceMotion: boolean
}) => {
  const tiles = useMemo(() => buildTiles(), [])
  return (
    <div className={styles.center}>
      <div className={styles.montage} aria-hidden>
        {tiles.map((t, i) => (
          <div
            key={i}
            className={`${styles.tile} ${reduceMotion ? styles.tileStill : ""}`}
            style={
              {
                left: t.left,
                top: t.top,
                width: t.size,
                height: t.size * 1.32,
                "--rot": `${t.rot}deg`,
                "--drift": `${t.drift}px`,
                "--ddur": `${t.dur}s`,
                "--ddelay": `${t.delay}s`,
                background: t.grad,
                zIndex: t.z,
                opacity: t.op,
              } as React.CSSProperties
            }
          >
            <img
              src={`${process.env.PUBLIC_URL}/img/vaultWrapped/mem${
                (i % MEMORY_COUNT) + 1
              }.jpg`}
              alt=""
              onError={(e) => {
                ;(e.currentTarget as HTMLImageElement).style.opacity = "0"
              }}
            />
          </div>
        ))}
      </div>
      <div className={styles.montageScrim} aria-hidden />

      <div className={styles.volumeContent}>
        <Reveal delay={0.15}>
          <span className={styles.eyebrow}>YOU'VE BEEN BUSY</span>
        </Reveal>
        <CountUp
          target={memories}
          reduceMotion={reduceMotion}
          className={styles.bigStat}
        />
        <Reveal delay={0.3}>
          <span className={styles.bigStatLabel}>memories sealed away</span>
        </Reveal>
        <Reveal delay={0.5}>
          <p className={styles.volumeSub}>
            tucked into <b>{albums}</b> private albums — every one of them
            visible to exactly one person on earth.
          </p>
        </Reveal>
      </div>
    </div>
  )
}

/* ================================================================== */
/* Chapter 2 — protection                                               */
/* ================================================================== */
const PROTECT_STATS = [
  { value: "0", label: "break-ins. Not one." },
  { value: "312", label: "sketchy unlock attempts blocked" },
  { value: "100%", label: "encrypted on your device" },
]
const ChapterProtection = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <div className={styles.center}>
    <motion.div
      className={styles.shieldWrap}
      initial={{ scale: 0.7, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      {!reduceMotion && <span className={styles.shieldPulse} aria-hidden />}
      <span className={styles.shieldPulse2} aria-hidden />
      <ShieldGlyph className={styles.shieldBig} />
      <Check className={styles.shieldCheck} />
    </motion.div>

    <Reveal delay={0.25}>
      <span className={styles.eyebrow}>AND IT HELD THE LINE</span>
    </Reveal>
    <Reveal delay={0.4}>
      <h2 className={styles.medTitle}>Nobody got in.</h2>
    </Reveal>

    <div className={styles.statChips}>
      {PROTECT_STATS.map((s, i) => (
        <motion.div
          key={i}
          className={styles.chip}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 + i * 0.12, duration: 0.5 }}
        >
          <span className={styles.chipValue}>{s.value}</span>
          <span className={styles.chipLabel}>{s.label}</span>
        </motion.div>
      ))}
    </div>

    <Reveal delay={1.0}>
      <p className={styles.volumeSub}>
        Zero-knowledge encryption means your memories are scrambled before they
        ever leave your phone. Even we can't peek.
      </p>
    </Reveal>
  </div>
)

/* ================================================================== */
/* Chapter 3 — status / identity                                        */
/* ================================================================== */
const ChapterStatus = ({ reduceMotion }: { reduceMotion: boolean }) => {
  const sparks = useMemo(() => buildSparks(), [])
  return (
    <div className={styles.center}>
      {!reduceMotion && (
        <div className={styles.sparks} aria-hidden>
          {sparks.map((s, i) => (
            <span
              key={i}
              className={styles.spark}
              style={
                {
                  left: s.left,
                  top: s.top,
                  width: s.size,
                  height: s.size,
                  "--sdelay": `${s.delay}s`,
                  "--sdur": `${s.dur}s`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}

      <motion.div
        className={styles.badge}
        initial={{ scale: 0.5, opacity: 0, rotate: -8 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className={styles.badgeGlow} aria-hidden />
        <span className={styles.badgeRing} aria-hidden />
        <span className={styles.badgePct}>8%</span>
        <span className={styles.badgeTop}>TOP</span>
      </motion.div>

      <Reveal delay={0.45}>
        <span className={styles.eyebrow}>YOUR RANK THIS YEAR</span>
      </Reveal>
      <Reveal delay={0.6}>
        <h2 className={styles.medTitle}>
          You're a top&nbsp;8%
          <br />
          <span className={styles.accentText}>Vault Keeper.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.8}>
        <p className={styles.volumeSub}>
          You protected more moments than 92% of vaults this year. That's a lot
          of life worth keeping.
        </p>
      </Reveal>
    </div>
  )
}

/* ================================================================== */
/* Chapter 4 — the offer (funnels to yearly)                            */
/* ================================================================== */
const PERKS = [
  {
    Icon: svg.featureIcons.CloudVault,
    text: "Unlimited encrypted cloud backup",
  },
  {
    Icon: svg.featureIcons.Breakin,
    text: "Break-in alerts & intruder capture",
  },
  { Icon: svg.featureIcons.Decoy, text: "Decoy vault & app disguise" },
  { Icon: svg.featureIcons.Star, text: "Ad-free, every feature unlocked" },
]
const ChapterOffer = ({
  hasTrial,
  yearlyPrice,
  perMonthEquivalent,
  monthlyPrice,
  savings,
  selected,
  setSelected,
  onPurchase,
  topInset,
  bottomInset,
}: {
  hasTrial: boolean
  yearlyPrice: string
  perMonthEquivalent: string
  monthlyPrice: string
  savings: number
  selected: "yearly" | "monthly"
  setSelected: (s: "yearly" | "monthly") => void
  onPurchase: () => void
  topInset: number
  bottomInset: number
}) => (
  <div className={styles.offer}>
    <div
      className={styles.offerScroll}
      style={{ paddingTop: topInset + 70, paddingBottom: bottomInset + 18 }}
    >
      <Reveal delay={0.1}>
        <span className={styles.eyebrow}>YOUR 2026 STARTS NOW</span>
      </Reveal>
      <Reveal delay={0.2}>
        <h2 className={styles.offerTitle}>
          Make next year your
          <br />
          <span className={styles.accentText}>safest one yet.</span>
        </h2>
      </Reveal>

      <motion.div
        className={styles.perks}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.32, duration: 0.5 }}
      >
        {PERKS.map((p, i) => (
          <div key={i} className={styles.perk}>
            <span className={styles.perkIcon}>
              <p.Icon />
            </span>
            <span className={styles.perkText}>{p.text}</span>
            <span className={styles.perkCheck}>
              <Check />
            </span>
          </div>
        ))}
      </motion.div>

      <motion.div
        className={styles.plans}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.42, duration: 0.5 }}
      >
        <button
          className={`${styles.yearCard} ${
            selected === "yearly" ? styles.yearCardActive : ""
          }`}
          onClick={() => setSelected("yearly")}
          aria-pressed={selected === "yearly"}
        >
          <span className={styles.cardShine} aria-hidden />
          <span className={styles.bestRibbon}>SAVE {savings}%</span>
          <div className={styles.yearHead}>
            <span
              className={`${styles.radio} ${
                selected === "yearly" ? styles.radioOn : ""
              }`}
              aria-hidden
            >
              {selected === "yearly" && <Check />}
            </span>
            <div className={styles.yearHeadText}>
              <span className={styles.yearName}>A full year, sealed</span>
              <span className={styles.yearSub}>
                {hasTrial ? "7 days free, then billed yearly" : "billed yearly"}
              </span>
            </div>
          </div>
          <div className={styles.priceBlock}>
            <div className={styles.priceRow}>
              <span className={styles.priceMain}>{yearlyPrice}</span>
              <span className={styles.pricePer}>/year</span>
            </div>
            <div className={styles.priceSub}>
              just {perMonthEquivalent}/mo · billed annually
            </div>
          </div>
        </button>

        <button
          className={`${styles.monthlyLine} ${
            selected === "monthly" ? styles.monthlyLineOn : ""
          }`}
          onClick={() => setSelected("monthly")}
          aria-pressed={selected === "monthly"}
        >
          Prefer monthly? {monthlyPrice}/mo — standard rate
        </button>
      </motion.div>

      <motion.div
        className={styles.ctaWrap}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.54, duration: 0.5 }}
      >
        <button className={styles.cta} onClick={onPurchase}>
          <span className={styles.ctaShimmer} aria-hidden />
          <span className={styles.ctaLabel}>
            {selected === "yearly" && hasTrial
              ? "Start my free week"
              : "Continue"}
          </span>
          <span className={styles.ctaArrow}>→</span>
        </button>
        <p className={styles.ctaNote}>
          Unlocks instantly · Cancel anytime in one tap
        </p>
      </motion.div>

      <div className={styles.trust}>
        <div className={styles.trustItem}>
          <span className={styles.trustStrong}>4.8★</span>
          <span className={styles.trustLabel}>App Store</span>
        </div>
        <span className={styles.trustSep} />
        <div className={styles.trustItem}>
          <span className={styles.trustStrong}>10M+</span>
          <span className={styles.trustLabel}>vaults sealed</span>
        </div>
        <span className={styles.trustSep} />
        <div className={styles.trustItem}>
          <span className={styles.trustStrong}>Zero-knowledge</span>
          <span className={styles.trustLabel}>only you hold the key</span>
        </div>
      </div>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  </div>
)

/* ================================================================== */
/* Small building blocks                                                */
/* ================================================================== */
const Reveal = ({
  children,
  delay = 0,
}: {
  children: React.ReactNode
  delay?: number
}) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
)

const CountUp = ({
  target,
  reduceMotion,
  className,
}: {
  target: number
  reduceMotion: boolean
  className?: string
}) => {
  const [val, setVal] = useState(reduceMotion ? target : 0)
  useEffect(() => {
    if (reduceMotion) {
      setVal(target)
      return
    }
    let raf = 0
    let start = 0
    const dur = 1400
    const step = (ts: number) => {
      if (!start) start = ts
      const p = Math.min(1, (ts - start) / dur)
      const eased = 1 - Math.pow(1 - p, 3)
      setVal(Math.round(target * eased))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target, reduceMotion])
  return <span className={className}>{val.toLocaleString()}</span>
}

/* deterministic seeded tiles so the montage looks identical every render */
function rand(seed: number) {
  return Math.abs(Math.sin(seed) * 43758.5453) % 1
}
function buildTiles() {
  // two parallax layers of memory tiles scattered around the edges
  const positions = [
    { left: "-6%", top: "6%", size: 96, z: 1, op: 0.5 },
    { left: "62%", top: "2%", size: 120, z: 1, op: 0.55 },
    { left: "70%", top: "44%", size: 104, z: 1, op: 0.5 },
    { left: "-10%", top: "52%", size: 128, z: 1, op: 0.5 },
    { left: "8%", top: "78%", size: 92, z: 1, op: 0.45 },
    { left: "58%", top: "76%", size: 112, z: 1, op: 0.5 },
    { left: "34%", top: "-8%", size: 88, z: 1, op: 0.45 },
    { left: "40%", top: "84%", size: 80, z: 1, op: 0.4 },
  ]
  const grads = [
    "linear-gradient(135deg,#2b5ff5,#0a1f6b)",
    "linear-gradient(135deg,#ffaa45,#b3611c)",
    "linear-gradient(135deg,#4cc6c0,#0e5a63)",
    "linear-gradient(135deg,#7a5cff,#2b1f6b)",
  ]
  return positions.map((p, i) => ({
    ...p,
    rot: (rand(i + 1) - 0.5) * 26,
    drift: 10 + rand(i + 9) * 22,
    dur: 7 + rand(i + 3) * 7,
    delay: rand(i + 5) * 4,
    grad: grads[i % grads.length],
  }))
}
function buildSparks() {
  return Array.from({ length: 18 }, (_, i) => ({
    left: `${rand(i + 2) * 100}%`,
    top: `${rand(i + 40) * 100}%`,
    size: 3 + rand(i + 7) * 5,
    delay: rand(i + 11) * 2.4,
    dur: 1.8 + rand(i + 13) * 2.2,
  }))
}

/* ---- inline glyphs ---- */
const ShieldGlyph = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 2.6l7.2 2.7v6.1c0 4.6-3.1 7.9-7.2 9.6-4.1-1.7-7.2-5-7.2-9.6V5.3L12 2.6z"
      fill="currentColor"
      fillOpacity="0.12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
)

const Check = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    width="100%"
    height="100%"
  >
    <path
      d="M5 12.5l4.2 4.2L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default VaultWrappedPaywall
